import { useQueryClient, useMutation } from '@tanstack/vue-query'
import { computed, watch, ComputedRef } from 'vue'
import store from '@/store'
import { $http } from '@/plugins'
import { setUserGroups } from '@/plugins/group-control'
import type {
  UserMeType,
  UserFlagsType,
  UserOnboardingInfo,
  UpdateUserPayload,
} from '@/types/app-data/user.types'
import { PATIENT_UNIT, MemberUnitType } from '@/scripts/configs/patient-units'

export default function useUser() {
  const queryClient = useQueryClient()

  const userId = computed(() => store.getters['user/getUserId']) as ComputedRef<number>
  const userUnit = computed(() => store.getters['user/getUserUnit']) as ComputedRef<MemberUnitType>
  const isUserSet = computed(() => store.getters['user/userExist'])
  const authTokens = computed(() => ({
    accessToken: store.getters['user/authentication/accessToken'],
    refreshToken: store.getters['user/authentication/refreshToken'],
  }))

  const userGroups = computed(() => {
    if (!userId.value) return []

    const { isFLabsPt } = store.getters['user/flags']

    return [
      ...(!isFLabsPt ? [] : ['f-labs', `f-labs:${userUnit.value === PATIENT_UNIT.SWORD ? 'thrive' : userUnit.value}`]),
    ]
  })

  watch(isUserSet, () => {
    ['fetch-user-me', 'fetch-user-flags', 'fetch-user-clients']
      .forEach((key: string) => queryClient.removeQueries({ queryKey: [key] }))

    setUserGroups(userGroups.value)
  }, { immediate: true })

  async function fetchMe(): Promise<UserMeType> {
    return queryClient.fetchQuery({
      queryKey: ['fetch-user-me'],
      staleTime: Infinity,
      queryFn: async () => {
        const { data: rawUserMe } = await $http('user/me/fetchMe')

        return {
          id: rawUserMe.user_id,
          firstname: rawUserMe.firstname,
          lastname: rawUserMe.lastname,
          picture: rawUserMe.picture,
          gender: rawUserMe.gender,
          username: rawUserMe.username,
          email: rawUserMe.email,
          role: rawUserMe.role.toLowerCase(),
          direct_message_address: rawUserMe.direct_message_address,
          telephone: rawUserMe.telephone,
          phone: rawUserMe.phone,
          measurement_system: rawUserMe.measurement_system,
          language: rawUserMe.language,
          sendbird: rawUserMe.sendbird,
          externalPhoneNumber: rawUserMe.external_phone_number,
          unit: rawUserMe.professional_unit,
        }
      },
    })
  }

  async function fetchUserFlags(): Promise<UserFlagsType> {
    return queryClient.fetchQuery({
      queryKey: ['fetch-user-flags'],
      staleTime: Infinity,
      queryFn: async () => {
        const { data: { data: rawUserFlags } } = await $http('user/me/flags')

        return {
          isAIPt: rawUserFlags.is_ai || false,
          isLeadPt: rawUserFlags.is_lead || false,
          isTreatmentPt: rawUserFlags.is_treatment_pt || false,
          isIntakePt: rawUserFlags.is_intake_pt || false,
          isClinicalAssistantManager: rawUserFlags.is_clinical_assistant_manager || false,
          hasProviderEnabled: rawUserFlags.has_provider_enabled || false,
          isFLabsPt: rawUserFlags.flabs_flag || false,
        }
      },
    })
  }

  async function fetchUserOnboardingInfo(): Promise<UserOnboardingInfo | null> {
    return queryClient.fetchQuery({
      queryKey: ['fetch-user-onboarding-info', userId],
      staleTime: Infinity,
      queryFn: async () => {
        const { data: { information } } = await $http('general/user/getTherapistInfo', userId.value)

        return information
      },
    })
  }

  const { mutateAsync: updateUser } = useMutation({
    mutationFn: (payload: UpdateUserPayload) => $http('user/updateProfessional', userId.value, { body: payload }),
    onSuccess: () => {
      // TODO When can use setQueryData method here when app is a bit more stable and we ensure payloads and store data have the exact names
      queryClient.removeQueries({ queryKey: ['fetch-user-me'] })
    },
  })

  return {
    userId,
    userUnit,
    isUserSet,
    authTokens,
    fetchMe,
    fetchUserFlags,
    fetchUserOnboardingInfo,
    updateUser,
  }
}
